/**
 * Khai nữ / nam / bé của ĐƠN ngay trong khối "Chia việc trang điểm" — chỉ hiện
 * khi đơn chưa khai số người trang điểm (lỗi 04/11/2026, đơn doanh nghiệp
 * DN-312).
 *
 * Vì sao đặt ở ĐÂY chứ không chỉ ở thẻ thông tin đơn: khối chia việc chỉ lưu
 * được khi đơn đã khai, mà có loại đơn (họ hợp đồng báo giá) không màn nào có
 * ô khai — dòng chữ cũ "khai ở thông tin đơn trước" chỉ tới một ô không tồn
 * tại. Người phân ê-kíp đứng ngay đây thì khai ngay đây.
 *
 * Ghi qua cửa sửa đơn (`PUT /orders/:id`, cùng ba ô của màn tạo đơn), không
 * qua sổ công: đây là lời khai của ĐƠN, máy lương và trần số thợ đọc nó.
 * Gợi ý "Khai theo báo giá" chỉ ĐIỀN ô Nữ — người khai vẫn phải bấm "Lưu số
 * người", vì báo giá không biết trong đó mấy nam, mấy bé.
 */
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { usePatchOrder } from "@/features/orders/api";

type Key = "female" | "male" | "child";
const KEYS: Key[] = ["female", "male", "child"];
const LABEL: Record<Key, string> = { female: "Nữ", male: "Nam", child: "Bé dưới 12" };

function parse(raw: string): number | null {
  if (raw.trim() === "") return 0;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= 60 ? n : null;
}

export function MakeupSplitDeclare({
  orderId,
  canEdit,
  suggested,
}: {
  orderId: string;
  /** Qua được cửa sửa đơn — không thì chỉ còn câu nhắc. */
  canEdit: boolean;
  /** Số mặt báo giá bán (chỉ hợp đồng một buổi) — nút điền sẵn ô Nữ. */
  suggested?: number | undefined;
}) {
  const patch = usePatchOrder();
  const [raw, setRaw] = useState<Record<Key, string>>({ female: "", male: "", child: "" });

  if (!canEdit) {
    return (
      <div className="text-[11px] text-[var(--color-warning-text)]">
        Đơn chưa khai số người trang điểm — nhờ người sửa được đơn khai nữ, nam, bé rồi mới chia được theo loại
      </div>
    );
  }

  const parsed = { female: parse(raw.female), male: parse(raw.male), child: parse(raw.child) };
  const bad = KEYS.some((k) => parsed[k] === null);
  const total = KEYS.reduce((s, k) => s + (parsed[k] ?? 0), 0);
  const empty = KEYS.every((k) => raw[k].trim() === "");

  function save() {
    patch.mutate(
      {
        id: orderId,
        input: {
          makeupHeadcount: total,
          makeupMaleCount: parsed.male ?? 0,
          makeupChildCount: parsed.child ?? 0,
        },
      },
      {
        onSuccess: () => toast.success(`Đã khai ${total} người trang điểm cho đơn`),
        onError: (e: unknown) =>
          toast.error(e instanceof Error ? e.message : "Không lưu được số người trang điểm"),
      },
    );
  }

  return (
    <div
      role="group"
      aria-label="Khai người được trang điểm của đơn"
      className="space-y-1.5 rounded border border-dashed border-[var(--color-warning)]/50 p-2"
    >
      <div className="text-[11px] text-[var(--color-warning-text)]">
        Đơn chưa khai số người trang điểm — khai nữ, nam, bé của đơn ngay đây rồi chia cho từng thợ
      </div>
      {suggested !== undefined && suggested > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => setRaw({ female: String(suggested), male: "0", child: "0" })}
        >
          Khai theo báo giá ({suggested} người)
        </Button>
      ) : null}
      <div className="grid grid-cols-3 gap-1.5 md:grid-cols-[repeat(3,4.5rem)_minmax(0,1fr)] md:items-end">
        {KEYS.map((k) => (
          <label key={k} className="min-w-0">
            <span className="block text-[10px] text-muted-foreground">{LABEL[k]}</span>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={60}
              step={1}
              placeholder="0"
              value={raw[k]}
              aria-label={`${LABEL[k]} của đơn`}
              onChange={(e) => setRaw({ ...raw, [k]: e.target.value })}
              className="h-8 text-right tabular-nums"
            />
          </label>
        ))}
        <div className="col-span-3 flex items-center justify-end gap-2 md:col-span-1">
          <span
            className={`text-[11px] tabular-nums ${bad ? "text-[var(--color-danger-text)]" : "text-muted-foreground"}`}
            role={bad ? "alert" : undefined}
          >
            {bad ? "Mỗi ô nhập số nguyên từ 0 đến 60" : `Tổng ${total}`}
          </span>
          <Button type="button" size="sm" disabled={empty || bad || patch.isPending} onClick={save}>
            {patch.isPending ? "Đang lưu…" : "Lưu số người"}
          </Button>
        </div>
      </div>
    </div>
  );
}
